import moment from 'moment-timezone';

import { isPerfMetricsEnabled, measurePerf } from '../../debug/performance';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { MarketCountryCode, StockExchangeCodeType } from '../../models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../../models/gateway/types';
import { OHLCV } from '../../models/market-data/types';
import { isEnhancedChartsEnabled } from '../../store/market-data/charting/chartFeatures';
import { getDailyMaxDomainUsStocks } from '../../store/market-data/charting/dailySession.helpers';
import { ChartDataPayload } from '../../store/market-data/charting/types';
import { updateBigChartCacheOnLoadChartDataCompleted } from '../../store-util/BigChartCache';
import InitialSymbolsDataLoadingStatusCache, {
  InitialSymbolsDataLoadingStatusKey,
} from '../../store-util/InitialSymbolsDataLoadingStatusCache';
import { updateSmallChartDataCache } from '../../store-util/SmallChartDataCache';
import SubscriptionTimingTracker from '../../store-util/SubscriptionTimingTracker';
import TradePriceCache, { updateTradePriceCacheFromChartData } from '../../store-util/TradePriceCache';
import { parseBase64String, parseMarketDataSummaries } from '../../util/GatewayHelpers';
import { isEmpty } from '../../util/typeGuards';
import { WebSocketEventData } from '../custom-event/types';
import { LATE_RESPONSE_THRESHOLD_MS } from '../web-socket/constants';
import { LoggerService } from '../logger/Logger.service';
import { GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { chunkSymbols, fillMarketDataSummaryGaps } from './helper';
import { SymbolBucketSubscriptionRequestType } from './types';

interface PendingUnsubscription {
  symbols: string[];
  bucket: GWMarketDataSummaryBucket;
  stockExchangeCode?: StockExchangeCodeType;// Optional because FX symbols don't have a stock exchange code
  countryDesignation?: MarketCountryCode; // Optional because FX symbols don't have a country designation
  isBigChart: boolean;
  timeoutId: ReturnType<typeof setTimeout>;
}

/**
 * What the app believes it is subscribed to. Kept separate from `subscripitonRequests`, which is the log of
 * individual gateway requests: large symbol lists are chunked into several requests, so that log cannot
 * answer "am I already subscribed to this symbol" without double counting.
 */
type ActiveSubscription = {
  symbols: string[];
  bucket: GWMarketDataSummaryBucket;
  stockExchangeCode?: StockExchangeCodeType;
  countryDesignation: MarketCountryCode;
  isBigChart: boolean;
};

const LOG_PREFIX = '[BASE-MDS-SUBS]';
const logger = LoggerService.getLogger();

/**
 * Base Market Data Summary Subscription Service
 */
export abstract class BaseMDSSubscriptionService extends BaseSubscriptionService<SymbolBucketSubscriptionRequestType> {
  private pendingUnsubscriptions: PendingUnsubscription[] = [];

  private activeSubscriptions: ActiveSubscription[] = [];

  public subscribe(
    symbol: string,
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    const activeSubscription = this.getActiveSubscriptionByBucketAndExchange(
      bucket,
      stockExchangeCode,
      countryDesignation,
      isBigChart,
    );

    if (!activeSubscription) {
      this.activeSubscriptions.push({
        bucket,
        symbols: [ symbol ],
        stockExchangeCode,
        countryDesignation,
        isBigChart,
      });

      const request: SymbolBucketSubscriptionRequestType = {
        bucket,
        symbols: [ symbol ],
        stockExchangeCode,
        optionalParams: { isBigChart: String(isBigChart), countryDesignation },
        ...this.getDefaultRequestParams(),
      };
      this.sendRequestToGw(request);
      return;
    }

    if (activeSubscription.symbols.includes(symbol)) { // This symbol already in the subscription request list!
      return;
    }

    activeSubscription.symbols.push(symbol); // add symbol to related list
    const request: SymbolBucketSubscriptionRequestType = {
      bucket,
      symbols: [ symbol ],
      stockExchangeCode,
      optionalParams: { isBigChart: String(isBigChart), countryDesignation },
      ...this.getDefaultRequestParams(),
    };
    this.sendRequestToGw(request);
  }

  private cancelPendingUnsubscriptions(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(pending => {
      if (pending.bucket === bucket
        && pending.stockExchangeCode === stockExchangeCode
        && pending.countryDesignation === countryDesignation
        && pending.isBigChart === isBigChart) {
        const hasOverlap = pending.symbols.some(symbol => symbols.includes(symbol));
        if (hasOverlap) {
          clearTimeout(pending.timeoutId);
          return false; // Remove from pending list
        }
      }
      return true; // Keep in pending list
    });
  }

  private scheduleDelayedUnsubscription(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    const timeoutId = setTimeout(() => {
      this.executeUnsubscription(symbols, bucket, stockExchangeCode, countryDesignation, isBigChart);
      // Remove from pending list
      this.pendingUnsubscriptions = this.pendingUnsubscriptions.filter(p => p.timeoutId !== timeoutId);
    }, PredefinedMilisecondsTypeEnum.fiveSeconds);

    this.pendingUnsubscriptions.push({
      symbols,
      bucket,
      stockExchangeCode,
      countryDesignation,
      isBigChart,
      timeoutId,
    });
  }

  private executeUnsubscription(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    const symbolChunks = chunkSymbols(symbols);
    symbolChunks.forEach(symbolChunk => {
      this.gatewayService.sendRequest({
        ...this.getDefaultRequestParams(),
        bucket,
        symbols: symbolChunk,
        stockExchangeCode,
        optionalParams: { isBigChart: String(isBigChart), countryDesignation },
        messageType: this.getUnsubscriptionRequestType(),
      });
    });
  }

  public subscribeList(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    this.cancelPendingUnsubscriptions(symbols, bucket, stockExchangeCode, countryDesignation, isBigChart);

    const activeSubscription = this.getActiveSubscriptionByBucketAndExchange(
      bucket,
      stockExchangeCode,
      countryDesignation,
      isBigChart,
    );

    if (!activeSubscription) {
      this.activeSubscriptions.push({
        bucket,
        symbols: [ ...symbols ],
        stockExchangeCode,
        countryDesignation,
        isBigChart,
      });

      const symbolChunks = chunkSymbols(symbols);

      symbolChunks.forEach(symbolChunk => {
        const request: SymbolBucketSubscriptionRequestType = {
          bucket,
          symbols: symbolChunk,
          stockExchangeCode,
          optionalParams: { isBigChart: String(isBigChart), countryDesignation },
          ...this.getDefaultRequestParams(),
        };
        this.sendRequestToGw(request);
      });
      return;
    }

    const notRequestedList = symbols.filter(item => !activeSubscription?.symbols.includes(item)) || [];

    if (isEmpty(notRequestedList)) { // All symbols are already added to subscription request list!
      return;
    }

    const symbolChunks = chunkSymbols(notRequestedList);
    // Record every new symbol once, before chunking - the chunks are a transport detail
    activeSubscription.symbols = [ ...activeSubscription.symbols, ...notRequestedList ];

    symbolChunks.forEach(symbolChunk => {
      const request: SymbolBucketSubscriptionRequestType = {
        bucket,
        symbols: symbolChunk,
        stockExchangeCode,
        optionalParams: { isBigChart: String(isBigChart), countryDesignation },
        ...this.getDefaultRequestParams(),
      };
      this.sendRequestToGw(request);
    });
  }

  public unsubscribe(
    symbol: string,
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    const activeSubscription = this.getActiveSubscriptionByBucketAndExchange(
      bucket,
      stockExchangeCode,
      countryDesignation,
      isBigChart,
    );

    if (!activeSubscription) { // There is no request in the list by the given bucket and exchange!
      return;
    }

    if (!activeSubscription.symbols.includes(symbol)) { // There is no request in the list by the given symbol!
      return;
    }

    activeSubscription.symbols = activeSubscription.symbols.filter(item => item !== symbol); // remove only given symbol from the list

    this.scheduleDelayedUnsubscription([ symbol ], bucket, stockExchangeCode, countryDesignation, isBigChart);
  }

  public unsubscribeList(
    symbols: string[],
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): void {
    const activeSubscription = this.getActiveSubscriptionByBucketAndExchange(
      bucket,
      stockExchangeCode,
      countryDesignation,
      isBigChart,
    );

    if (!activeSubscription) { // There is no request in the list by the given bucket and exchange!
      return;
    }

    const requestedList = symbols.filter(item => activeSubscription?.symbols.includes(item)) || [];

    if (isEmpty(requestedList)) { // The symbols are not in the subscription request list!
      return;
    }

    activeSubscription.symbols = activeSubscription.symbols.filter(
      item => !requestedList.includes(item),
    ); // remove given symbols from the list

    this.scheduleDelayedUnsubscription(requestedList, bucket, stockExchangeCode, countryDesignation, isBigChart);
  }

  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};
    const { messageType: messageName, traceIdentifier, summaries: summariesRaw } = data;
    const messageType: GatewayResponseType = GatewayResponseType[messageName] as GatewayResponseType ?? messageName;

    const traceIdentifierRequest = this.subscripitonRequests.find(
      item => item.traceIdentifier === traceIdentifier,
    ) as SymbolBucketSubscriptionRequestType;

    if (!traceIdentifierRequest) {
      // Stock and FX summaries share one response type, so a service may legitimately see another's
      // response. An unmatched trace id that no service claims means a dropped or duplicated request.
      logger.warn(`${LOG_PREFIX} DataSummaryResponse with no matching subscription request - orphaned response`, {
        messageType,
        traceIdentifier,
        knownTraceIdentifiers: this.subscripitonRequests.map(item => item.traceIdentifier),
      });
      return;
    }

    // Mark answered first, so the late-request sweeper ignores it even if parsing below fails.
    traceIdentifierRequest.respondedAt = moment.utc();

    if (traceIdentifierRequest.requestTime) {
      const elapsedMs = moment.utc().diff(traceIdentifierRequest.requestTime);
      if (elapsedMs > LATE_RESPONSE_THRESHOLD_MS) {
        logger.warn(`${LOG_PREFIX} Late DataSummaryResponse`, {
          traceIdentifier,
          elapsedMs,
          thresholdMs: LATE_RESPONSE_THRESHOLD_MS,
          bucket: traceIdentifierRequest.bucket,
          stockExchangeCode: traceIdentifierRequest.stockExchangeCode,
          symbols: traceIdentifierRequest.symbols,
        });
      }
    }

    try {
      const summaries = parseBase64String(summariesRaw);
      let parsedData: Record<string, OHLCV[]> = {};
      const { symbols, bucket, optionalParams, stockExchangeCode } = traceIdentifierRequest;
      const symbol = symbols?.[0];
      const hasNoDataOnBackend = summaries.hasNoData;
      const symbolsWithNoData: string[] = [];

      if (hasNoDataOnBackend) {
        symbols.forEach(item => {
          parsedData[item] = [];
          symbolsWithNoData.push(item);
        });
      } else {
        parsedData = measurePerf(
          'mds.parseMarketDataSummaries',
          { bucket: traceIdentifierRequest.bucket },
          () => parseMarketDataSummaries(summaries.rows),
        );

        // Add missing symbols as empty arrays (gateway may not return all requested symbols)
        // This allows gap filling to process them and use previous close values
        symbols.forEach(item => {
          if (!parsedData[item]) {
            symbolsWithNoData.push(item);
            parsedData[item] = [];
          }
        });
      }
      if (!isEmpty(symbolsWithNoData)) {
        logger.warn(`${LOG_PREFIX} DataSummaryResponse missing data for some requested symbols`, {
          traceIdentifier,
          bucket,
          stockExchangeCode,
          requestedSymbols: symbols,
          missingSymbols: symbolsWithNoData,
          hasNoDataOnBackend,
        });
      }

      const isBigChart = optionalParams?.isBigChart === 'true';
      const countryDesignation = optionalParams?.countryDesignation || MarketCountryCode.UnitedStates;

      if ([ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ].includes(bucket)) {
        updateTradePriceCacheFromChartData({ bucket, data: parsedData }, stockExchangeCode);
      }

      const hasAnySymbolWithNoData = !isEmpty(symbolsWithNoData);

      // FX symbols don't have stockExchangeCode, so skip gap filling for them
      const isGapFilled = Boolean(stockExchangeCode);
      const filledData = isGapFilled
        ? measurePerf(
          'mds.fillMarketDataSummaryGaps',
          { bucket, symbolCount: symbols?.length ?? 0 },
          () => fillMarketDataSummaryGaps(parsedData, bucket, countryDesignation),
        )
        : parsedData;

      if (bucket === GWMarketDataSummaryBucket.OneDay) {
        updateSmallChartDataCache(filledData, traceIdentifier, {
          hasNoDataSymbols: hasAnySymbolWithNoData ? symbolsWithNoData : undefined,
          dailyMaxPoints: isGapFilled && isEnhancedChartsEnabled() ? getDailyMaxDomainUsStocks() : undefined,
        });
      }

      if (isBigChart) {
        const { priceData } = TradePriceCache.get(symbol);
        const lineClose = priceData?.lineClose;
        updateBigChartCacheOnLoadChartDataCompleted(
          {
            symbol,
            bucket,
            data: filledData[symbol],
          } as ChartDataPayload,
          traceIdentifier,
          lineClose,
        );
      }

      if (!isBigChart && isPerfMetricsEnabled()) {
        SubscriptionTimingTracker.markChart(symbols);
      }

      // eslint-disable-next-line import/no-named-as-default-member
      InitialSymbolsDataLoadingStatusCache.updateStatus(`chart_${bucket as '1D5m' | '3M1D'}` as InitialSymbolsDataLoadingStatusKey, true);
    } catch (error) {
      logger.error(`${LOG_PREFIX} handleGWWebsocketMessages - error parsing '${messageType}' - ${error}`);
    }
  }

  private getActiveSubscriptionByBucketAndExchange(
    bucket: GWMarketDataSummaryBucket,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation: MarketCountryCode = MarketCountryCode.UnitedStates,
    isBigChart: boolean = false,
  ): ActiveSubscription | undefined {
    return this.activeSubscriptions.find(
      item => item.bucket === bucket
        && item.stockExchangeCode === stockExchangeCode
        && item.countryDesignation === countryDesignation
        && item.isBigChart === isBigChart,
    );
  }
}
